import { listOf } from '@/lib/commandWords';

/**
 * The quick preview's words. [C1e §2]
 *
 * ⚠️ IN A `copy.ts` RATHER THAN INLINE, because prose that is exported is prose a test can
 * assert on — the standing argument behind every page-level `copy.ts`, one directory over.
 */
export const PREVIEW_COPY = {
  /**
   * ⚠️ [C3a §5a] `(2 of 4 named)` BESIDE THE POST'S COLUMN HEADING.
   *
   * §5a puts the count where the post is NAMED rather than at the far end of a row, on this page
   * and on the officer naming screen alike. `POST_COPY.bracketed` is the fill screen's own half
   * of the same shape; the two are separate because they read from different DTOs and neither
   * should import the other's copy file.
   */
  postCount: (named: number, required: number): string => `(${String(named)} of ${String(required)} named)`,
  openFull: 'Open full',
  /**
   * ⚠️ [C5c §6a] THE RECORD PAGE's OWN WORDS (`VIEW_COPY.print`), because it opens the same sheet — one
   * document, reached from two places, named the same in both.
   */
  print: 'Print order',
  close: 'Close preview',
  /**
   * ⚠️ §2b's figures, in the order the brief states them: required · sourced · named.
   * ⚠️ [amended C5c §5a] `reached` WENT — it counted Reached alone and so dropped an officer who
   * stood down after reaching. The order's attendance is a LINE now, in three words (`attendance`).
   */
  required: 'Required',
  sourced: 'Sourced',
  named: 'Named',
  /** [C5c §5a] Prefixes `presenceLine` — `Attendance: 7 present · 2 absent · 1 not arrived`. */
  attendance: 'Attendance',
  /** ⚠️ `—` FOR A NUMBER THAT DOES NOT EXIST YET, never a zero. See `arrangementSchema`. */
  none: '—',
  dutyPoints: (count: number): string => `${String(count)} duty point${count === 1 ? '' : 's'}`,
  policies: (count: number): string => `${String(count)} policies`,
  /** A card with no duty at all — a draft, or every duty detached. [P6b.2]: answer for it. */
  noDuty: 'No duty has been created for this place yet.',
  /** A card whose duty asks for nobody. Rare, and it must not render as a blank card. */
  noForce: 'This place asks for no force.',
  /**
   * ⚠️ [C5c §2] A COLUMN ASKED FOR ONCE ACROSS THE ORDER. On a covered card it says where the people
   * are named; on the anchor it says the number there is the whole order's. A covered card whose
   * only force is shared is NOT "no force" — somebody stands it — so that sentence is suppressed.
   */
  sharedFrom: (column: string, anchor: string): string => `${column} — shared, named under ${anchor}`,
  coversEvery: (column: string): string => `${column} covers every duty point`,
  namedOf: (named: number, required: number): string => `${String(named)} of ${String(required)}`,
  /**
   * ⚠️ [amended C1f §2a] `more` AND `nobodyNamed` ARE GONE WITH THE NAMES THEY QUALIFIED.
   * A force post now renders a count table, division-wise, and nothing else.
   */
  noDivisions: 'No division has been asked for this yet.',
  /** [C4a §4] A duty point's zone, beside its title. The arrangement view reads the same words. */
  zone: (zone: string): string => `Zone: ${zone}`,
  /** [C5c §4] A duty point's sector, on a line of its own. The arrangement view reads the same words. */
  sector: (sector: string): string => `Sector: ${sector}`,
  /*
   * ⚠️ [C1h §4a] THE MATRIX'S TWO AXIS WORDS, AND THEY ARE `ForceGrid`'s OWN.
   *
   * §4a says the preview is the create-arrangement force grid READ BACK — "the operator already
   * knows how to read it and it matches the paper". Different words for the same two axes would
   * make two screens of one table, which is `labels.ts`' second-vocabulary defect.
   *
   * ⚠️ `Total`, NOT `TOTAL REQUIRED`. There it labels the DEMAND an operator types; here it
   * labels a SUM of `n of m` cells, which is a different quantity and must not borrow its name.
   */
  source: 'Source',
  /**
   * ⚠️ [C3a §5] A VEHICLE COLUMN STATES A COUNT AND NOTHING ELSE — see `previewRosterRowSchema.kind`.
   * "named" is a claim about people, and [C2a §5a] removed exactly that treatment from a vehicle
   * line one screen over.
   */
  vehicleCount: (required: number): string => `(${String(required)} required)`,
  /**
   * ⚠️ [P7A §3] A VEHICLE'S BODY CELL IS ITS REQUIRED COUNT ALONE — never `0 of 2`, which is a vehicle
   * short-fall; the client's rule is that a vehicle states what it requires and nothing else.
   */
  vehicleCell: (required: number): string => String(required),
  /** ⚠️ [C1f §2b] A supervisory seat with nobody in it still renders — [P6b.2]'s rule. */
  noCommandNamed: 'Nobody named',
  /*
   * ⚠️ [C6a] A ROLE ASKED OF A DIVISION — where its positions stand, in the codes an operator reads
   * (`CommandContacts`). Distinct codes, joined as a sentence joins them; never a number.
   */
  awaitedFrom: (codes: readonly string[]): string => `Awaited from ${listOf(codes)}`,
  handedTo: (codes: readonly string[]): string => `Handed to ${listOf(codes)} · awaited`,
  askedOf: (codes: readonly string[]): string => `Asked of ${listOf(codes)}`,
  /**
   * ⚠️ [C6a] AN EMPTY POSITION ON AN ORDER THAT IS OVER OR WITHDRAWN (`awaitedIsLive`). "Awaited" there
   * would promise a name nobody can still give, where the printed order and the bell say nothing.
   */
  notNamedBy: (codes: readonly string[]): string => `Not named by ${listOf(codes)}`,
  loading: 'Loading the order',
  failed: 'That order could not be loaded.',
} as const;
